/**
 * Drag-to-rotate turntable input with inertia (mouse, pen and touch).
 * Horizontal drags spin; vertical touch pans are left to the page (canvas uses touch-action: pan-y).
 */
export class Spin {
  yaw: number;
  /** Idle rotation speed in rad/s (0 = stay put). */
  auto = 0;
  private vel = 0;
  private sampleVel = 0;
  private drag: { id: number; x: number; t: number; sx: number; sy: number; moved: boolean } | null = null;
  private suppressClick = false;

  constructor(initialYaw = 0) {
    this.yaw = initialYaw;
  }

  get dragging(): boolean {
    return this.drag !== null;
  }

  /** Wires pointer events on el. onActive(true/false) brackets a drag. Returns the detach function. */
  attach(el: HTMLElement, onActive: (on: boolean) => void): () => void {
    const down = (e: PointerEvent): void => {
      if (this.drag || (e.pointerType === 'mouse' && e.button !== 0)) return;
      this.drag = { id: e.pointerId, x: e.clientX, t: e.timeStamp, sx: e.clientX, sy: e.clientY, moved: false };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone */
      }
      this.vel = 0;
      this.sampleVel = 0;
      onActive(true);
    };
    const move = (e: PointerEvent): void => {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      const dt = Math.max(4, e.timeStamp - d.t) / 1000;
      d.x = e.clientX;
      d.t = e.timeStamp;
      if (!d.moved && Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 6) d.moved = true;
      const rad = dx * 0.0115;
      this.yaw += rad;
      this.sampleVel = this.sampleVel * 0.5 + (rad / dt) * 0.5;
    };
    const end = (e: PointerEvent): void => {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;
      // A pause before letting go means no fling.
      const still = (e.timeStamp - d.t) / 1000 > 0.09;
      this.vel = still || e.type === 'pointercancel' ? 0 : Math.max(-14, Math.min(14, this.sampleVel));
      if (d.moved) {
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 0);
      }
      onActive(false);
    };
    const click = (e: MouseEvent): void => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('click', click, true);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
      el.removeEventListener('click', click, true);
      this.drag = null;
    };
  }

  /** Advances inertia/auto-rotation. Returns true if the yaw changed. */
  tick(dt: number): boolean {
    if (this.drag) return true;
    const before = this.yaw;
    this.vel += (this.auto - this.vel) * (1 - Math.exp(-dt * 2.4));
    if (Math.abs(this.vel) < 1e-4) this.vel = 0;
    this.yaw += this.vel * dt;
    return this.yaw !== before;
  }
}
